import React, { useState } from "react";

import styles from "./CreateSubscriptionForm.module.css";
import { useMutation } from "@tanstack/react-query";
import { axiosConfig } from "../../../../axios/axiosConfig";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

const CreateSubscriptionForm = ({
  formType = "",
  activePlan,
  setAllPlanDetails,
  setOpenSubscriptionForm,
}) => {
  const { user } = useSelector((state) => state.auth);
  const USERID = user?.userDetails?.id;

  const initialPlanInputState = {
    name: formType === "edit" ? activePlan?.name : "",
    code: formType === "edit" ? activePlan?.plan_code : "",
    description: formType === "edit" ? activePlan?.description : "",
    features: formType === "edit" ? activePlan?.plan_description : "",
    price: formType === "edit" ? activePlan?.price : "",
    alertCount: formType === "edit" ? activePlan?.alert : "",
    interval: formType === "edit" ? activePlan?.interval : "",
    isPlanActive: formType === "edit" ? activePlan?.active : true,
    intervalUnit: "months",
    plan_order: formType === "edit" ? Number(activePlan?.plan_order) : "",
    is_visible_for_user:
      formType === "edit" ? activePlan?.is_visible_for_user : true,
    is_addon: formType === "edit" ? !!activePlan?.is_addon : false,
    trial_supported: formType === "edit" ? !!activePlan?.trial_supported : false,
    trial_days: formType === "edit" ? (activePlan?.trial_days ?? "") : "",
    base_price: formType === "edit" ? (activePlan?.base_price ?? "") : "",
    discounted_price: formType === "edit" ? (activePlan?.discounted_price ?? "") : "",
    trial_amount: formType === "edit" ? (activePlan?.trial_amount ?? "") : "",
    is_recurring_plan: formType === "edit" ? !!activePlan?.is_recurring_plan : false,
  };

  // planInput state will be initialized with activePlan if "editing" or updating subscription form
  const [planInput, setPlanInput] = useState(initialPlanInputState);

  const {
    name,
    code,
    description,
    features,
    price,
    alertCount,
    interval,
    intervalUnit,
    plan_order,
    is_visible_for_user,
    is_addon,
  } = planInput;

  // onchange handler for all inputs
  const inputOnChange = (event) => {
    setPlanInput((prevState) => {
      if (
        event.target.name === "isPlanActive" ||
        event.target.name === "is_visible_for_user" ||
        event.target.name === "is_addon" ||
        event.target.name === "trial_supported" ||
        event.target.name === "is_recurring_plan"
      ) {
        return {
          ...prevState,
          [event.target.name]: event.target.checked,
        };
      }
      return {
        ...prevState,
        [event.target.name]: event.target.value,
      };
    });
  };

  const METHOD_TYPE = formType === "edit" ? "patch" : "post";

  // CREATE NEW or update subscription plan
  const postSubPlan = useMutation({
    mutationFn: async () =>
      mutatePlan(METHOD_TYPE, planInput, USERID, activePlan?.id),
    onSuccess: (data) => {
      if (formType === "edit") {
        setAllPlanDetails((prevPlans) => {
          return prevPlans.map((plan) => {
            if (plan.id === activePlan?.id) {
              return data.response;
            } else {
              return plan;
            }
          });
        });
      } else if (formType === "create") {
        setAllPlanDetails((prevPlans) => {
          return [...prevPlans, data.response];
        });
      }

      // reseting planInputs to null
      setPlanInput(initialPlanInputState);

      window.alert("Plan updated/created successfully!");
    },
    onError: (error) => {
      console.log("data", error);
      const errMsg =
        error?.response?.data?.response?.plan_code?.[0] ||
        error?.response?.data?.response ||
        error?.message ||
        "Something went wrong";
      toast.error(errMsg);
    },
  });

  // HANDLE CREATE / POST NEW MARQUEE
  const handleMutatePlan = (event) => {
    // event.preventDefault();

    const consent = window.confirm(
      `*** PLAN SUMMARY! ***\nPLAN NAME: ${name} | PLAN CODE: ${code}\nFEATURES: ${features}\nPRICE: ${price} | ALERTS: ${alertCount} | PLAN PERIOD: ${interval} ${intervalUnit}\nAre you sure you want to ${formType} Plan?`
    );

    if (!consent) {
      return;
    }

    postSubPlan.mutate();
    setOpenSubscriptionForm(false);
  };

  // reset button
  const handleReset = (event) => {
    event.preventDefault();
    setPlanInput(initialPlanInputState);
  };

  const disableCreateBtn =
    !name ||
    name.trim() === "" ||
    !code ||
    code.trim() === "" ||
    !description ||
    description.trim() === "" ||
    !features ||
    features.trim() === "" ||
    !price ||
    price.trim() === "" ||
    !interval ||
    !intervalUnit ||
    intervalUnit.trim() === "" ||
    !plan_order ||
    +plan_order < 1;

  return (
    <form
      action=""
      style={{ margin: "0px", marginTop: "10px" }}
      className={styles.subscriptionFormContainer}
    >
      <h2 className={styles.formTitle}>
        {formType === "edit" ? "Update Plan" : "Create Plan"}
      </h2>

      <div className={styles.formGroup}>
        <label htmlFor="name">Plan Name</label>
        <input
          type="text"
          className={styles.formControl}
          id="name"
          name="name"
          value={planInput.name}
          onChange={inputOnChange}
        />
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="code">Plan Code</label>
        <input
          disabled={formType === "edit"}
          type="text"
          placeholder="Eg. PREMIUM349912M"
          className={styles.formControl}
          id="code"
          name="code"
          value={planInput.code}
          onChange={inputOnChange}
        />
      </div>
      <div className={styles.formGroup}>
        <label htmlFor="description">Description</label>
        <input
          type="text"
          className={styles.formControl}
          id="description"
          name="description"
          value={planInput.description}
          onChange={inputOnChange}
        />
      </div>

      {/* Plan Features/Description (plan_description) */}
      <div className={styles.formGroup}>
        <label htmlFor="features">Features</label>
        <input
          type="text"
          placeholder="feature1, feature2, feature3"
          className={styles.formControl}
          id="features"
          name="features"
          value={planInput.features}
          onChange={inputOnChange}
        />
      </div>
      <div className={styles.formGroup}>
        <label htmlFor="price">Price</label>
        <input
          type="number"
          className={styles.formControl}
          id="price"
          name="price"
          value={planInput.price}
          onChange={inputOnChange}
          placeholder="Enter subscription amount in rupees"
        />
      </div>

      {/* BASE PRICE */}
      <div className={styles.formGroup}>
        <label htmlFor="base_price">Base Price</label>
        <input
          type="number"
          step="0.01"
          min="0"
          className={styles.formControl}
          id="base_price"
          name="base_price"
          value={planInput.base_price}
          onChange={inputOnChange}
          placeholder="e.g. 5000.00"
        />
      </div>

      {/* DISCOUNTED PRICE */}
      <div className={styles.formGroup}>
        <label htmlFor="discounted_price">Discounted Price</label>
        <input
          type="number"
          step="0.01"
          min="0"
          className={styles.formControl}
          id="discounted_price"
          name="discounted_price"
          value={planInput.discounted_price}
          onChange={inputOnChange}
          placeholder="e.g. 2499.00"
        />
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="interval">Interval</label>
        <input
          type="number"
          min="1"
          max="12"
          placeholder="Number of months, years, etc."
          className={styles.formControl}
          id="interval"
          name="interval"
          value={planInput.interval}
          onChange={inputOnChange}
        />
      </div>

      {/* Interval Units */}
      <div className={styles.formGroup}>
        <label htmlFor="intervalUnit">Interval Unit</label>
        <select
          // className={styles.selectScreenInput}
          className={styles.formControl}
          id="intervalUnit"
          name="intervalUnit"
          value={planInput.intervalUnit}
          onChange={inputOnChange}
        >
          {intervalOptions.map((item) => {
            return (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            );
          })}
        </select>
      </div>

      {/* ALERT COUNT */}
      <div className={styles.formGroup}>
        <label htmlFor="alertCount">Alert Count</label>
        <input
          type="number"
          min="0"
          max="5000"
          placeholder="Number of Alerts"
          className={styles.formControl}
          id="alertCount"
          name="alertCount"
          value={planInput.alertCount}
          onChange={inputOnChange}
        />
      </div>
      {/* PLAN ORDER */}
      <div className={styles.formGroup}>
        <label htmlFor="plan_order">Plan Order</label>
        <input
          type="number"
          min="1"
          placeholder="Enter display order"
          className={styles.formControl}
          id="plan_order"
          name="plan_order"
          value={planInput.plan_order}
          onChange={inputOnChange}
        />
      </div>

      {/* PLAN ACTIVE? */}
      <div className={styles.formGroup}>
        <label htmlFor="isPlanActive">Active?</label>
        <input
          type="checkbox"
          name="isPlanActive"
          id="isPlanActive"
          // className={styles.formControl}
          value={planInput.isPlanActive}
          onChange={inputOnChange}
          checked={planInput.isPlanActive}
        />
      </div>

      {/* IS VISIBLE FOR USER */}
      <div className={styles.formGroup}>
        <label htmlFor="is_visible_for_user">Visible for User</label>
        <input
          type="checkbox"
          id="is_visible_for_user"
          name="is_visible_for_user"
          checked={planInput.is_visible_for_user}
          onChange={inputOnChange}
        />
      </div>
      {/* IS ADDON PLAN */}
      <div className={styles.formGroup}>
        <label htmlFor="is_addon">Is Addon?</label>
        <input
          type="checkbox"
          id="is_addon"
          name="is_addon"
          checked={planInput.is_addon}
          onChange={inputOnChange}
        />
      </div>

      {/* TRIAL SUPPORTED */}
      <div className={styles.formGroup}>
        <label htmlFor="trial_supported">Trial Supported?</label>
        <input
          type="checkbox"
          id="trial_supported"
          name="trial_supported"
          checked={planInput.trial_supported}
          onChange={inputOnChange}
        />
      </div>

      {/* TRIAL DAYS */}
      <div className={styles.formGroup}>
        <label htmlFor="trial_days">Trial Days</label>
        <input
          type="number"
          min="0"
          placeholder="Number of trial days"
          className={styles.formControl}
          id="trial_days"
          name="trial_days"
          value={planInput.trial_days}
          onChange={inputOnChange}
        />
      </div>

      {/* IS RECURRING PLAN */}
      <div className={styles.formGroup}>
        <label htmlFor="is_recurring_plan">Is Recurring Plan?</label>
        <input
          type="checkbox"
          id="is_recurring_plan"
          name="is_recurring_plan"
          checked={planInput.is_recurring_plan}
          onChange={inputOnChange}
        />
      </div>

      {/* TRIAL AMOUNT */}
      <div className={styles.formGroup}>
        <label htmlFor="trial_amount">Trial Amount</label>
        <input
          type="number"
          step="0.01"
          min="0"
          placeholder="e.g. 1.00"
          className={styles.formControl}
          id="trial_amount"
          name="trial_amount"
          value={planInput.trial_amount}
          onChange={inputOnChange}
        />
      </div>

      {/* reset button */}
      <div className={styles.resetBtnContainer}>
        <button className={styles.resetBtn} onClick={handleReset}>
          clear
        </button>
      </div>

      {/* button container */}
      <div className={styles.btnContainer}>
        {formType === "edit" ? (
          <button
            // disabled={disableCreateBtn}
            // className="btn editPlanBtn"
            className={`${styles.btn} ${styles.editPlanBtn}`}
            onClick={handleMutatePlan}
          >
            update
          </button>
        ) : (
          <button
            type="submit"
            disabled={disableCreateBtn}
            // className="btn createPlanBtn"
            className={`${styles.btn} ${styles.createPlanBtn}`}
            onClick={handleMutatePlan}
          >
            Create Plan
          </button>
        )}
      </div>
    </form>
  );
};

export default CreateSubscriptionForm;

const intervalOptions = [
  { id: 1, value: "years", label: "years" },
  { id: 2, value: "months", label: "months" },
  { id: 3, value: "weeks", label: "weeks" },
  // { id: 4, value: "days", label: "days" },
];

// MUTATE (CREATE & UPDATE) subscription plan
export const mutatePlan = async (method, plan_input, USERID, plan_id) => {
  if (!USERID) return;
  const {
    name,
    code,
    description,
    features,
    price,
    interval,
    intervalUnit,
    alertCount,
    isPlanActive,
    plan_order,
    is_visible_for_user,
    is_addon,
    trial_supported,
    trial_days,
    base_price,
    discounted_price,
    trial_amount,
    is_recurring_plan,
  } = plan_input;

  let apiURL = "/subscription/subscription_plan"; // for post method only
  apiURL =
    method === "patch"
      ? `/subscription/subscription_plan?plan_id=${plan_id}`
      : apiURL;

  const res = await axiosConfig({
    method: method,
    url: apiURL,
    data: {
      userid: USERID,
      name: name,
      plan_code: code,
      description: description,
      plan_description: features,
      price: price,
      interval: interval,
      interval_unit: intervalUnit,
      alert: alertCount,
      active: isPlanActive,
      plan_order: Number(plan_order),
      is_visible_for_user: Boolean(is_visible_for_user),
      is_addon: Boolean(is_addon),
      trial_supported: Boolean(trial_supported),
      trial_days: trial_days !== "" ? Number(trial_days) : null,
      base_price: base_price !== "" ? parseFloat(base_price) : null,
      discounted_price: discounted_price !== "" ? parseFloat(discounted_price) : null,
      trial_amount: trial_amount !== "" ? parseFloat(trial_amount) : null,
      is_recurring_plan: Boolean(is_recurring_plan),
    },
  });
  return res.data;
};
